import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import {
  Container,
} from '@safelyq/bulma-ui-library';
import { MenuList } from './components';
import { useData, useTinyMCEKey, useOrdersCount, useCampaignsCount } from './hooks';
import { useDispatch } from 'react-redux';
import firestore from '@firebase';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import {
  setBusinessName,
  setBusinessCategory,
  setBusinessFeatures,
  setBusinessId,
  setIsAssosiate,
  setIsAdmin,
  setBusinessSubLevels,
  setBusinessSubscriptionPlan,
  setBusinessSubscription,
  setBusinessTimeZone,
  setBusinessApprovalStatus,
  setTinyMceKey,
  setBusinessLogoUrl,
} from 'Redux/BusinessSlice/Business';
import { setUnpaidOrders, setActiveCampaings, setDashboardDataChanged } from 'Redux/MiscSlice/Misc';

const StyledContainer = styled(Container).attrs(() => ({}))`
  padding: 0;
  height: 100vh;
  background-color: #111827;
`;

export const AdminDrawer = ({ handleMenuClose = () => { } }) => {

  const dispatch = useDispatch();
  let { pathname } = useLocation();
  if (pathname.charAt(pathname.length - 1) === '/') {
    pathname = pathname.slice(0, pathname.length - 1);
  }

  let businessId = pathname.substring(pathname.lastIndexOf('/') + 1);
  const id = parseInt(businessId);

  // Fetch business's frequently used data/variables
  const { business, onLoadBusiness } = useData(id);
  const { tinyMceKey, onLoadTinyMCEKey } = useTinyMCEKey();
  const { getOrdersCount } = useOrdersCount();
  const { getCampaignsCount } = useCampaignsCount();

  useEffect(() => {
    id && dispatch(setBusinessId(id))
  }, [id])

  useEffect(() => {
    onLoadBusiness({ variables: { id } });
    onLoadTinyMCEKey();
  }, [])

  // Snapshot listener on the orders collection: when it runs, request the order summary
  // and update the unpaid orders count in the global Misc state.
  useEffect(() => {
    if (!id) return;

    const unsubscribe = onSnapshot(
      query(
        collection(firestore, "orders"),
        where('businessId', '==', `${id}`)
      ),
      async () => {
        getOrdersCount({
          variables: {
            filterInput: {
              businessId: Number(id),
              skip: 0,
              take: 1000,
              searchText: '',
              status: 'Unpaid',
              source: '',
              startDate: null,
              endDate: null,
              orderView: 'ACTIVE',
            },
          },
        }).then((response) => {
          const unpaidCount = response?.data?.getBusinessPosOrders?.orders?.length;
          dispatch(setUnpaidOrders(unpaidCount));
          dispatch(setDashboardDataChanged(true));
        });
      }
    );

    return () => unsubscribe();
  }, [id]);

  // update the active campaigns count in the global Misc state.
  useEffect(() => {
    if (!id) return;
    const fetchData = async () => {
      const response = await getCampaignsCount({
        variables: {
          selectCampaignsInput: {
            businessId: id,
            searchText: '',
            statuses: ['ACTIVE'],
            skip: 0,
            take: 1000,
            endDate: null,
            startDate: null,
          },
        },
      });

      const campaigns = response?.data?.getCampaigns?.campaigns || [];
      const activeCount = campaigns?.length;
      dispatch(setActiveCampaings(activeCount));

    };
    fetchData();
  }, [id, getCampaignsCount]);

  const categoryName = business?.businessCategory?.name;
  const googleAllowed = business?.businessCategory?.features?.googleReserveAllowed;
  const couponAllowed = business?.businessCategory?.features?.couponAllowed;
  const campaignZipcodeAllowed = business?.businessCategory?.features?.campaignZipcodeAllowed;
  const isAdmin = business?.businessPermissions?.isAdmin;
  const isAssociate = business?.businessPermissions?.isAssociate;
  const businessSubLevels = business?.businessSubLevels;
  const businessTimeZone = business?.timeZone;
  const businessSubscriptionPlan = business?.businessSubscription?.subscriptionPlan?.name;
  const businessSubscription = business?.businessSubscription;
  const businessApprovalStatus = business?.businessApprovalStatus?.status;
  const businessLogoUrl = business?.logoUrl;

  /******* Dispatch Data to the global State *********/
  // Set Business Name to global state every time it changes
  useEffect(() => {
    if (business?.name || business?.businessCategory?.name) {
      business?.name && dispatch(setBusinessName(business?.name));
    }

  }, [business?.name])

  //  Set Business Category to global state every time it changes
  useEffect(() => {
    if (business?.businessCategory?.name) {
      // on every refresh we need new updated data as super admin might have changed it.
      // if (business?.businessCategory?.name && !businessCategoryFeaturesGlobal) {
      business?.businessCategory?.name && dispatch(setBusinessCategory(business?.businessCategory));
    }

  }, [categoryName, googleAllowed, couponAllowed, campaignZipcodeAllowed])

  // Set Features allowed in the current subscription plan to global state if the state is empty
  useEffect(() => {
    if (business?.businessSubscription?.subscriptionPlan?.features) {
      dispatch(setBusinessFeatures(business?.businessSubscription?.subscriptionPlan?.features))
    }
  }, [business?.businessSubscription?.subscriptionPlan?.features])

  // Set business sub levels to the global state
  useEffect(() => {
    if (businessSubLevels) {
      dispatch(setBusinessSubLevels(businessSubLevels))
    }
  }, [businessSubLevels])

  // Set businessSubscriptionPlan to the global state
  useEffect(() => {
    if (businessSubscriptionPlan) {
      dispatch(setBusinessSubscriptionPlan(businessSubscriptionPlan))
    }
  }, [businessSubscriptionPlan])

  // Set businessSubscription (name, tier, features) to the global state
  useEffect(() => {
    if (businessSubscriptionPlan) {
      dispatch(setBusinessSubscription(businessSubscription))
    }
  }, [businessSubscription])

  // Set businessTimeZone to the global state
  useEffect(() => {
    if (businessTimeZone) {
      dispatch(setBusinessTimeZone(businessTimeZone))
    }
  }, [businessTimeZone])

  // Set isAssociate and isAdmin to the global state
  useEffect(() => {
    if (isAssociate !== null && isAssociate !== undefined) {
      dispatch(setIsAssosiate(isAssociate));
    }
    if (isAdmin !== null && isAdmin !== undefined) {
      dispatch(setIsAdmin(isAdmin));
    }
  }, [isAssociate, isAdmin])

  // set business approval status
  useEffect(() => {
    if (businessApprovalStatus) {
      dispatch(setBusinessApprovalStatus(businessApprovalStatus))
    }
  }, [businessApprovalStatus])

  // Set tinyMceKey to the global state
  useEffect(() => {
    if (tinyMceKey) {
      dispatch(setTinyMceKey(tinyMceKey))
    }
  }, [tinyMceKey])

  // Set business logo url to the global state
  useEffect(() => {
    dispatch(setBusinessLogoUrl(businessLogoUrl || null))
  }, [businessLogoUrl])

  // const isError = isLoading || error || !name;

  return (
    <StyledContainer>
      <aside class='menu'>
        <MenuList
          businessId={id}
          handleMenuClose={handleMenuClose}
        />
      </aside>
    </StyledContainer>
  );
};
